import { DialogM } from "@common/components/alert/dialog/Dialog"
import { notifyBugsnag } from "@common/utils/initBugsnag"
import NetInfo from "@react-native-community/netinfo"
import axios from "axios"
import {
    API_URL,
    apiConstants,
    VAPOR_FILE_UPLOAD,
} from "../../../constants/apiConstants"
import { authConstants } from "../../../constants/authConstants"
import { vaporStore } from "../../../services"
import { authService } from "../../../services/authService"
import { networkFailuresOwned } from "../../../utility/networkFailureOwner"
import { store } from "../../storeConfig/store"

// Crash safety net: an undefined serviceFunction here used to crash with
// "Cannot read property 'name' of undefined" deep inside the action creators.
// Returning a no-op + notifying lets us trace the offending call site without
// taking down the screen.
const NOOP_ACTION = { type: "@@api/NOOP_UNDEFINED_SERVICE" }
const noopThunk = () => {}

function isValidServiceFunction(serviceFunction, callerName) {
    if (serviceFunction && serviceFunction.name) return true
    notifyBugsnag(
        new Error(
            `${callerName} called with undefined or unnamed serviceFunction`
        )
    )
    return false
}

/**
 * La session a-t-elle changé de compte depuis que la requête est partie ? — le jeton qui est parti
 * n'est plus celui de la session : une bascule de compte (`SWITCH_ACCOUNT`) est passée entre les deux.
 */
function sessionChangedSince(sentAuthorization) {
    const token = store.getState()?.data?.session?.object?.token
    return (
        !!sentAuthorization &&
        !!token &&
        sentAuthorization !== "Bearer " + token
    )
}

export function setNetworkOn() {
    return { type: apiConstants.NETWORK_ON }
}

export function setNetworkOff() {
    return { type: apiConstants.NETWORK_OFF }
}

export function apiIdle(serviceFunction) {
    if (!isValidServiceFunction(serviceFunction, "apiIdle")) return NOOP_ACTION
    return { type: serviceFunction.name + apiConstants._IDLE }
}

/**
 * Les options d'une requête, en dernier argument de `apiRequest` et de `vaporFileUpload`.
 *
 * `offlineDialog` (défaut `true`) : hors ligne, la requête pose elle-même la boîte « Pas de
 * connexion Internet » et son « Réessayer ». `false` : elle ne dit rien, l'échec hors ligne
 * (`isOffline: true`) part comme d'habitude, et c'est l'appelant qui décide quoi montrer et quand
 * réessayer — un moteur qui tient sa propre file d'envoi, par exemple.
 *
 * `onResult` (défaut absent) : appelé une fois la requête jouée, avec ce que les réducteurs ont
 * reçu — `{ ok: true, data, allData }` au succès, `{ ok: false, error }` à l'échec (hors ligne
 * compris). Les actions `_REQUEST` / `_SUCCESS` / `_FAILURE` partent comme d'habitude : un moteur
 * attend la réponse sans perdre ce que les réducteurs en font.
 *
 * `freshNetworkState` (défaut `false`) : l'état du réseau est RELU avant d'envoyer
 * (`NetInfo.refresh`), au lieu de l'état gardé (`NetInfo.fetch`). Sur le web, celui-ci peut rester
 * « hors ligne » après une coupure : le module web de NetInfo n'écoute que `navigator.connection`,
 * dont Chrome ne redit pas toujours le retour. La relecture met aussi l'état gardé à jour, pour toutes
 * les requêtes qui suivent.
 *
 * Hors ligne, la boîte se tait aussi tant qu'un écran dit lui-même ses échecs réseau
 * (`networkFailureOwner`).
 */
const DEFAULT_REQUEST_OPTIONS = { offlineDialog: true }

// Action API call
export function apiRequest(
    serviceFunction,
    routeParams = {},
    data = {},
    urlParams = {},
    onUploadProgress = null,
    passedToken = null,
    requestOptions = DEFAULT_REQUEST_OPTIONS
) {
    if (!isValidServiceFunction(serviceFunction, "apiRequest")) return noopThunk

    function request() {
        return {
            type: serviceFunction.name + apiConstants._REQUEST,
            routeParams,
            data,
            urlParams,
        }
    }

    function success(responseData, sentAuthorization, requestedAt) {
        return {
            type: serviceFunction.name + apiConstants._SUCCESS,
            routeParams,
            data: responseData.data,
            all_data: responseData,
            urlParams,
            // L'ordre entre les réponses, que l'ordre d'arrivée ne donne pas (`reducers/data/liveListFreshness.js`).
            requestedAt,
            respondedAt: Date.now(),
            // Le jeton parti n'est plus celui de la session : la réponse décrit un autre compte, et
            // n'écrit pas le compte affiché (`reducers/data/index.js`).
            ...(sessionChangedSince(sentAuthorization) && {
                fromPreviousSession: true,
            }),
        }
    }

    function failure(error) {
        return {
            type: serviceFunction.name + apiConstants._FAILURE,
            routeParams,
            error,
            urlParams,
        }
    }

    const dispatchRequestIfInternet = (dispatch) => {
        let token

        if (passedToken) {
            token = passedToken
        } else {
            token = store.getState()?.data?.session?.object?.token
        }

        if (token) {
            axios.defaults.headers.common["Authorization"] = "Bearer " + token
        }

        const networkState = requestOptions?.freshNetworkState
            ? NetInfo.refresh()
            : NetInfo.fetch()
        networkState.then((state) => {
            if (state.isConnected) {
                dispatch(request())

                // L'en-tête est commun à toute l'app : une autre requête a pu le changer depuis
                // qu'on l'a posé. Celui qui part est celui qu'axios lit maintenant.
                const sentAuthorization =
                    axios.defaults.headers.common["Authorization"]
                const requestedAt = Date.now()
                serviceFunction(routeParams, data, urlParams, onUploadProgress)
                    .then((response) => {
                        dispatch(success(response.data, sentAuthorization, requestedAt))
                        requestOptions?.onResult?.({
                            ok: true,
                            data: response.data?.data,
                            allData: response.data,
                        })
                    })
                    .catch((error) => {
                        const failed = {
                            ...error?.response?.data,
                            status: error?.response?.status,
                        }
                        dispatch(failure(failed))
                        requestOptions?.onResult?.({ ok: false, error: failed })

                        if (
                            error?.response &&
                            error?.response?.status === 401
                        ) {
                            dispatch({ type: authConstants.LOGOUT_401 })
                        } else if (
                            error?.response &&
                            error?.response?.status === 419
                        ) {
                            dispatch(
                                apiRequestWithPrerequest(
                                    serviceFunction,
                                    routeParams,
                                    data,
                                    urlParams,
                                    onUploadProgress
                                )
                            )
                        }
                    })
            } else {
                // Offline: notify any state machine listening on this service
                // (e.g. useRequestsBatch) so it can reset, then surface the
                // global retry dialog. Without the failure dispatch, the
                // request status stays at REQUEST forever and consumers hang.
                const offline = {
                    status: 0,
                    message: "Pas de connexion Internet",
                    isOffline: true,
                }
                dispatch(failure(offline))
                requestOptions?.onResult?.({ ok: false, error: offline })
                if (
                    requestOptions?.offlineDialog === false ||
                    networkFailuresOwned()
                )
                    return
                DialogM.show({
                    text1: "Pas de connexion Internet",
                    text2: "",
                    buttonText1: "Réessayer",
                    onPress: () => {
                        dispatchRequestIfInternet(dispatch)
                    },
                    buttonText2: "Annuler",
                })
            }
        })
    }

    return (dispatch) => {
        dispatchRequestIfInternet(dispatch)
    }
}

export function vaporFileUploadIdle() {
    return { type: VAPOR_FILE_UPLOAD + apiConstants._IDLE }
}

// Action API call upload file to vapor s3
export function vaporFileUpload(
    file,
    options = {},
    formId,
    requestOptions = DEFAULT_REQUEST_OPTIONS
) {
    function request() {
        return { type: VAPOR_FILE_UPLOAD + apiConstants._REQUEST, file, formId }
    }

    function success(response) {
        return {
            type: VAPOR_FILE_UPLOAD + apiConstants._SUCCESS,
            response,
            file,
            formId,
        }
    }

    function failure(error) {
        return {
            type: VAPOR_FILE_UPLOAD + apiConstants._FAILURE,
            error,
            file,
            formId,
        }
    }

    const dispatchRequestIfInternet = (dispatch) => {
        NetInfo.fetch().then((state) => {
            if (state.isConnected) {
                dispatch(request())

                vaporStore(file, {
                    ...options,
                    baseURL: API_URL,
                })
                    .then((response) => {
                        dispatch(success(response))
                    })
                    .catch((error) => {
                        dispatch(failure(error))
                    })
            } else {
                // Offline: notify any state machine listening on this upload
                // (e.g. useRequestsBatch) so it can reset, then surface the
                // global retry dialog. Without the failure dispatch, the
                // upload status stays at REQUEST forever and consumers hang.
                dispatch(
                    failure({
                        status: 0,
                        message: "Pas de connexion Internet",
                        isOffline: true,
                    })
                )
                if (
                    requestOptions?.offlineDialog === false ||
                    networkFailuresOwned()
                )
                    return
                DialogM.show({
                    text1: "Pas de connexion Internet",
                    text2: "",
                    buttonText1: "Réessayer",
                    onPress: () => {
                        dispatchRequestIfInternet(dispatch)
                    },
                    buttonText2: "Annuler",
                })
            }
        })
    }

    return (dispatch) => {
        dispatchRequestIfInternet(dispatch)
    }
}

// Action API call
export function apiRequestWithPrerequest(
    serviceFunction,
    routeParams = {},
    data = {},
    urlParams = {},
    onUploadProgress = null
) {
    if (!isValidServiceFunction(serviceFunction, "apiRequestWithPrerequest"))
        return noopThunk

    function request() {
        return {
            type: serviceFunction.name + apiConstants._REQUEST,
            routeParams,
            data,
            urlParams,
        }
    }

    // Same shape as apiRequest's answers: the slot hands the params of the
    // request back with its answer, re-sent or not.
    function success(data) {
        return {
            type: serviceFunction.name + apiConstants._SUCCESS,
            routeParams,
            data,
            urlParams,
        }
    }

    function failure(error) {
        return {
            type: serviceFunction.name + apiConstants._FAILURE,
            routeParams,
            error,
            urlParams,
        }
    }

    return (dispatch) => {
        dispatch(request())

        authService
            .getCsrfToken()
            .then(() => {
                serviceFunction(routeParams, data, urlParams, onUploadProgress)
                    .then((response) => {
                        dispatch(success(response.data.data))
                    })
                    .catch((error) => {
                        dispatch(
                            failure({
                                ...error?.response?.data,
                                status: error?.response?.status,
                            })
                        )
                    })
            })
            .catch((error) => {
                dispatch(
                    failure({
                        ...error?.response?.data,
                        status: error?.response?.status,
                    })
                )
            })
    }
}
